/** @jsxImportSource theme-ui */
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Text, Box, Flex } from '@infotrack/zenith-ui';
import { useTopFiveDetailData } from '../hooks/useTopFiveDetailData';
import { useUpdateTopFive } from '../hooks/useUpdateTopFive';
import { TopFiveDetailHeader } from '../components/topFiveDetailHeader';
import { StaffInfoCard } from '../components/staffInfoCard';
import { TopFiveItemsList } from '../components/topFiveItemsList';
import { pageStyles, contentStyles } from './topFiveDetailPage.styles';

type PendingItemChange = {
  topFiveItemId: number;
  completion?: number;
  status?: string;
  result?: string;
  weight?: number;
};

export const TopFiveDetailPage = () => {
  const { topFiveId } = useParams<{ topFiveId: string }>();

  const topFiveIdNumber = useMemo(() => {
    return topFiveId ? parseInt(topFiveId, 10) : undefined;
  }, [topFiveId]);

  const { topFiveDetailData, isLoading, error } = useTopFiveDetailData({
    topFiveId: topFiveIdNumber,
    enabled: !!topFiveIdNumber && !isNaN(topFiveIdNumber),
  });

  const { staffTopFive, topFiveItems, permissions } = topFiveDetailData;

  // State management for pending changes
  const [pendingChanges, setPendingChanges] = useState<
    Map<number, PendingItemChange>
  >(new Map());

  // Clear pending changes when data changes
  React.useEffect(() => {
    if (topFiveItems.length > 0) {
      setPendingChanges(new Map()); // Clear pending changes when new data arrives
    }
  }, [topFiveItems]);

  // Update hook
  const updateTopFive = useUpdateTopFive({
    topFiveId: topFiveIdNumber || 0,
    onSuccess: () => {
      setPendingChanges(new Map()); // Clear pending changes
    },
  });

  // Derived state
  const hasPendingChanges = pendingChanges.size > 0;
  const mergedItems = useMemo(
    () =>
      topFiveItems.map(item => {
        const pending = pendingChanges.get(item.topFiveItemId!);
        return pending ? { ...item, ...pending } : item;
      }),
    [topFiveItems, pendingChanges]
  );

  // Change handlers
  const handleItemChange = useCallback(
    (itemId: number, changes: Partial<PendingItemChange>) => {
      setPendingChanges(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(itemId) || { topFiveItemId: itemId };
        const updated = { ...existing, ...changes };

        // Check if this change sets result to "NA"
        const isChangingToNA = changes.result === 'NA';

        if (isChangingToNA) {
          // Get the original item to find its current weight
          const originalItem = topFiveItems.find(
            item => item.topFiveItemId === itemId
          );

          if (originalItem) {
            // Get the current effective weight (considering any pending changes)
            const currentPending = newMap.get(itemId);
            const currentWeight =
              currentPending?.weight ?? originalItem.weight ?? 0;
            const weightToRedistribute = currentWeight;

            // Set this item's weight to 0
            updated.weight = 0;

            // Find all items that are NOT "NA" (excluding the current item)
            const eligibleItems = topFiveItems.filter(item => {
              if (item.topFiveItemId === itemId) return false;

              // Check both pending changes and original result
              const pendingChange = newMap.get(item.topFiveItemId!);
              const effectiveResult = pendingChange?.result ?? item.result;

              return effectiveResult !== 'NA';
            });

            if (eligibleItems.length > 0 && weightToRedistribute > 0) {
              const distributedWeight =
                weightToRedistribute / eligibleItems.length;

              // Update each eligible item
              eligibleItems.forEach(item => {
                const itemPending = newMap.get(item.topFiveItemId!) || {
                  topFiveItemId: item.topFiveItemId!,
                };
                const itemCurrentWeight =
                  itemPending.weight ?? item.weight ?? 0;
                const newWeight = itemCurrentWeight + distributedWeight;
                // Round to 2 decimal places
                const roundedWeight = Math.round(newWeight * 100) / 100;
                newMap.set(item.topFiveItemId!, {
                  ...itemPending,
                  weight: roundedWeight,
                });
              });
            }
          }
        }

        // Set the updated item
        newMap.set(itemId, updated);

        // Check if values are back to original - if so, remove from pending
        const original = topFiveItems.find(
          item => item.topFiveItemId === itemId
        );
        if (original) {
          // Normalize null and empty string for result comparison
          const normalizeResult = (val: string | null | undefined) =>
            val === null || val === undefined ? '' : val;

          // If all changed fields match original, remove from pending
          const hasActualChanges =
            (updated.completion !== undefined &&
              updated.completion !== original.completion) ||
            (updated.status !== undefined &&
              updated.status !== original.status) ||
            (updated.result !== undefined &&
              normalizeResult(updated.result) !==
                normalizeResult(original.result)) ||
            (updated.weight !== undefined &&
              updated.weight !== original.weight);

          if (!hasActualChanges) {
            newMap.delete(itemId);
          } else {
            newMap.set(itemId, updated);
          }
        } else {
          newMap.set(itemId, updated);
        }

        return newMap;
      });
    },
    [topFiveItems]
  );

  const handleCancel = useCallback(() => {
    setPendingChanges(new Map());
  }, []);

  const handleUpdate = useCallback(async () => {
    const changes = Array.from(pendingChanges.values());
    if (changes.length > 0) {
      try {
        await updateTopFive.updateTopFiveItems(changes);
        // Success is handled by the onSuccess callback in useUpdateTopFive
      } catch (error) {
        // Error is already handled by the hook with toast
        console.error('Update failed:', error);
      }
    }
  }, [pendingChanges, updateTopFive]);

  if (isLoading) {
    return (
      <Box sx={pageStyles} role="status" aria-live="polite">
        <Spinner fullSize aria-label="Loading TOP 5 details" />
      </Box>
    );
  }

  if (error || !staffTopFive) {
    return (
      <Flex sx={pageStyles} as="main">
        <TopFiveDetailHeader />
        <Box sx={contentStyles} role="alert">
          <Text variant="medium" color="error">
            Failed to load TOP 5 details. Please try again later.
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex sx={pageStyles} as="main">
      <TopFiveDetailHeader
        hasPendingChanges={hasPendingChanges}
        onCancel={handleCancel}
        onUpdate={handleUpdate}
        isUpdating={updateTopFive.isLoading}
      />

      <Box sx={contentStyles} as="section" aria-label="TOP 5 Details">
        <StaffInfoCard staff={staffTopFive.staff} />

        <TopFiveItemsList
          items={mergedItems}
          onItemChange={handleItemChange}
          permissions={permissions || undefined}
          showActions={true}
          emptyTitle="No TOP 5 items available"
          emptySubtitle="This TOP 5 has no items to display"
          isInteractive={permissions?.isEditable ?? false}
          topFiveId={topFiveIdNumber}
        />
      </Box>
    </Flex>
  );
};
